/**
 * XlsxImportMapModal
 *
 * Full-screen map modal matching the NI Map page layout:
 *   - Top bar: municipality (read-only) | barangay (read-only) | address input | Search
 *   - Left: map with NI-style "Click on the map to set new location" toolbar on top
 *   - Right panel: LAYERS tab | SUBSCRIBER tab
 *
 * Save requires: address field non-empty AND pin confirmed on map.
 * Saves complete_address + coordinates to backend.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  GeoJSON,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../../api/client";
import { geocodeSubscriberAddress } from "../../services/geocodingService";
import {
  SERVICE_AREA_CENTER,
  SERVICE_AREA_ZOOM,
  getBarangayCoords,
} from "../../data/barangayGeodata";

// ---------------------------------------------------------------------------
// Pin icon
// ---------------------------------------------------------------------------
function makePinIcon(saved) {
  const color = saved ? "#22c55e" : "#ef4444";
  const glow = saved ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)";
  return L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="width:32px;height:32px;border-radius:50%;background:${color};border:3px solid #fff;
        box-shadow:0 3px 10px ${glow};display:flex;align-items:center;justify-content:center;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
      <div style="width:3px;height:10px;background:${color};"></div>
      <div style="width:8px;height:8px;border-radius:50%;background:${color};border:2px solid #fff;
        box-shadow:0 2px 4px ${glow};"></div>
    </div>`,
    iconSize: [32, 50],
    iconAnchor: [16, 50],
    popupAnchor: [0, -50],
  });
}

// ---------------------------------------------------------------------------
// Point-in-polygon (ray casting) for GeoJSON features
// ---------------------------------------------------------------------------
function pointInRing(lat, lng, ring) {
  // ring is [[lng, lat], ...] (GeoJSON coordinate order)
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInGeoJSONFeature(lat, lng, feature) {
  const geom = feature?.geometry;
  if (!geom) return false;
  if (geom.type === "Polygon") return pointInRing(lat, lng, geom.coordinates[0]);
  if (geom.type === "MultiPolygon") return geom.coordinates.some((poly) => pointInRing(lat, lng, poly[0]));
  return false;
}

/**
 * Returns true if the point is inside the feature polygon OR within ~300m of
 * its bounding box. This handles GeoJSON boundaries that don't perfectly align
 * with Google's geocoded coordinates (streets near barangay edges).
 */
function pointInFeatureOrNear(lat, lng, feature) {
  if (pointInGeoJSONFeature(lat, lng, feature)) return true;
  // Bounding box fallback with ~300m buffer (≈0.003°)
  const geom = feature?.geometry;
  if (!geom) return false;
  const rings =
    geom.type === "Polygon" ? [geom.coordinates[0]] :
    geom.type === "MultiPolygon" ? geom.coordinates.map((p) => p[0]) : [];
  if (!rings.length) return false;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const ring of rings) {
    for (const [cx, cy] of ring) {
      if (cy < minLat) minLat = cy;
      if (cy > maxLat) maxLat = cy;
      if (cx < minLng) minLng = cx;
      if (cx > maxLng) maxLng = cx;
    }
  }
  const BUF = 0.008;
  return lat >= minLat - BUF && lat <= maxLat + BUF && lng >= minLng - BUF && lng <= maxLng + BUF;
}

// Barangay name labels (same pattern as NI Dashboard)
function BrgyLabels({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data || !map) return;
    const markers = [];
    (data.features || []).forEach((f) => {
      const name = f.properties?.brgy_name || "";
      if (!name) return;
      const centroid = L.geoJSON(f).getBounds().getCenter();
      const marker = L.marker(centroid, {
        icon: L.divIcon({
          className: "",
          html: `<span style="font-size:13px;font-weight:700;color:#1d4ed8;text-shadow:0 0 4px #fff,0 0 4px #fff,0 0 4px #fff;white-space:nowrap;pointer-events:none;letter-spacing:0.03em;">${name}</span>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
        interactive: false,
      });
      marker.addTo(map);
      markers.push(marker);
    });
    return () => markers.forEach((m) => map.removeLayer(m));
  }, [data, map]);
  return null;
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 16, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

function ZoomWatcher({ onZoomChange }) {
  useMapEvents({
    zoomend(e) {
      onZoomChange(e.target.getZoom());
    },
  });
  return null;
}

function MapClickToPlace({ active, onPlaced }) {
  const enabledAt = useRef(0);
  useEffect(() => { if (active) enabledAt.current = Date.now(); }, [active]);
  useMapEvents({
    click(e) {
      if (!active) return;
      if (Date.now() - enabledAt.current < 150) return;
      if (e.originalEvent?.target?.closest(".leaflet-popup, .leaflet-control")) return;
      onPlaced(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function XlsxImportMapModal({ record, onClose, onSaved }) {
  const [address, setAddress] = useState(record.complete_address || "");
  const [pin, setPin] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(15);
  const [searching, setSearching] = useState(false);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editLocConfirm, setEditLocConfirm] = useState(false);
  const [rightTab, setRightTab] = useState("layers");

  // Layer toggles
  const [showSatellite, setShowSatellite] = useState(false);
  const [showStreetMap, setShowStreetMap] = useState(true);
  const [showStreetLabels, setShowStreetLabels] = useState(true);
  const [showLandmarks, setShowLandmarks] = useState(false);
  const [showCityBoundaries, setShowCityBoundaries] = useState(true);
  const [showBrgyBoundaries, setShowBrgyBoundaries] = useState(false);
  const [showBrgyLabels, setShowBrgyLabels] = useState(true);

  // GeoJSON data
  const [brgyGeoJSON, setBrgyGeoJSON] = useState(null);
  const [cityGeoJSON, setCityGeoJSON] = useState(null);

  const initDone = useRef(false);

  // Load boundary GeoJSON
  useEffect(() => {
    fetch("/data/barangay_boundaries.geojson")
      .then((r) => r.json())
      .then(setBrgyGeoJSON)
      .catch(() => {});
    fetch("/data/city_boundaries.geojson")
      .then((r) => r.json())
      .then(setCityGeoJSON)
      .catch(() => {});
  }, []);

  // On mount: fly to existing coords or barangay centroid
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;
    const hasCoords =
      record.latitude && record.longitude &&
      !isNaN(parseFloat(record.latitude)) &&
      !isNaN(parseFloat(record.longitude));
    if (hasCoords) {
      const lat = parseFloat(record.latitude);
      const lng = parseFloat(record.longitude);
      setPin({ lat, lng });
      setFlyTarget([lat, lng]);
      setFlyZoom(18);
      if (record.location_saved) {
        setMsg({ type: "success", text: "Saved location loaded. Adjust pin if needed." });
      }
    } else {
      const coords = getBarangayCoords(record.municipality, record.barangay);
      if (coords) { setFlyTarget(coords); setFlyZoom(15); }
    }
  }); // eslint-disable-line

  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 7000);
    return () => clearTimeout(t);
  }, [msg]);

  const handleSearch = useCallback(async () => {
    const q = address.trim();
    if (!q) return;
    setSearching(true);
    setMsg(null);
    setEditLocConfirm(false);
    try {
      const result = await geocodeSubscriberAddress({
        address: q,
        barangay: record.barangay,
        municipality: record.municipality,
        latitude: null,
        longitude: null,
      });

      // Reject barangay/municipality-level fallbacks — those mean the street wasn't found
      const streetFound =
        result.success &&
        result.latitude &&
        result.longitude &&
        result.accuracy !== "barangay" &&
        result.accuracy !== "municipality";

      if (streetFound) {
        // Validate that the result is actually inside the subscriber's barangay
        let insideBrgy = true; // allow if boundary data not yet loaded
        if (brgyGeoJSON) {
          const expected = (record.barangay || "").trim().toUpperCase();
          const brgyFeature = (brgyGeoJSON.features || []).find((f) => {
            const name = (f.properties?.brgy_name || "").trim().toUpperCase();
            return name === expected || name.includes(expected) || expected.includes(name);
          });
          if (brgyFeature) {
            insideBrgy = pointInFeatureOrNear(result.latitude, result.longitude, brgyFeature);
          }
        }

        if (insideBrgy) {
          setPin({ lat: result.latitude, lng: result.longitude });
          setFlyTarget([result.latitude, result.longitude]);
          setFlyZoom(18);
          const isExact = result.accuracy === "exact" || result.accuracy === "address";
          setMsg({ type: isExact ? "success" : "warning", text: result.message });
        } else {
          setMsg({
            type: "error",
            text: `"${q}" was found but is outside Barangay ${record.barangay}. Please click on the map to place the pin manually.`,
          });
        }
      } else {
        setMsg({
          type: "error",
          text: `Street or address "${q}" cannot be located within Barangay ${record.barangay}. Please click on the map to place the pin manually.`,
        });
      }
    } catch {
      setMsg({ type: "error", text: "Search failed. Click the map to place the pin manually." });
    } finally {
      setSearching(false);
    }
  }, [address, record.barangay, record.municipality, brgyGeoJSON]); // eslint-disable-line

  const handleSave = async () => {
    if (!pin || !address.trim()) return;
    setSaving(true);
    try {
      await api.put(`/xlsx-imports/${record.id}`, { complete_address: address.trim() });
      const { data } = await api.put(`/xlsx-imports/${record.id}/coordinates`, {
        latitude: pin.lat,
        longitude: pin.lng,
      });
      onSaved({ ...data, complete_address: address.trim() });
      onClose();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Failed to save. Please try again." });
      setSaving(false);
      setEditLocConfirm(false);
    }
  };

  const canSave = address.trim().length > 0 && pin !== null;
  const pinIcon = makePinIcon(record.location_saved);

  return (
    <div className="fixed inset-0 z-[2000] flex flex-col bg-gray-100">

      {/* ── Top bar ── */}
      <div className="flex-shrink-0 bg-white border-b shadow-sm">
        <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">{record.customer_full_name}</p>
            <p className="text-xs text-gray-500">Set subscriber location</p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 text-lg leading-none disabled:opacity-50"
          >
            &times;
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-gray-500 font-medium">Municipality:</span>
            <span className="text-xs font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded px-2 py-1">
              {record.municipality || "—"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-gray-500 font-medium">Barangay:</span>
            <span className="text-xs font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded px-2 py-1">
              {record.barangay || "—"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={address}
              onChange={(e) => { setAddress(e.target.value); setEditLocConfirm(false); }}
              onKeyDown={(e) => e.key === "Enter" && !searching && !saving && handleSearch()}
              placeholder="Type street & house number, then Search"
              disabled={saving}
              className="w-72 border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={!address.trim() || searching || saving}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            >
              {searching ? (
                <span className="flex items-center gap-1.5">
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Searching…
                </span>
              ) : "Search"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Body: map + right panel ── */}
      <div className="flex-1 flex min-h-0 gap-4 p-4">

        {/* Map */}
        <div className="flex-1 relative isolate min-h-0 rounded-lg overflow-hidden shadow">

          {/* NI-style toolbar centred on top of the map */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1001]">
            {!editLocConfirm ? (
              <div className="bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3 whitespace-nowrap">
                📍 Click on the map to set new location
                <button
                  onClick={() => setEditLocConfirm(true)}
                  disabled={!canSave}
                  className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs font-semibold transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3 whitespace-nowrap">
                <span className="text-xs">Save this as subscriber's location?</span>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded text-xs font-semibold transition-colors"
                >
                  {saving ? "Saving…" : "Confirm"}
                </button>
                <button
                  onClick={() => setEditLocConfirm(false)}
                  disabled={saving}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded text-xs transition-colors"
                >
                  Back
                </button>
              </div>
            )}
          </div>

          {/* Geocode message */}
          {msg && (
            <div className={`absolute top-16 left-3 z-[1001] rounded-lg px-3 py-2 shadow text-xs font-medium max-w-xs border flex items-start gap-2 ${
              msg.type === "success" ? "bg-green-50 text-green-800 border-green-300" :
              msg.type === "warning" ? "bg-yellow-50 text-yellow-800 border-yellow-300" :
              msg.type === "info"    ? "bg-blue-50 text-blue-800 border-blue-300" :
              "bg-red-50 text-red-800 border-red-300"
            }`}>
              <span>{msg.text}</span>
              <button onClick={() => setMsg(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">✕</button>
            </div>
          )}

          {/* Coordinates */}
          {pin && (
            <div className="absolute bottom-3 left-3 z-[1000] bg-white/90 rounded-lg px-2.5 py-1.5 shadow border border-gray-200 text-[10px] text-gray-500 font-mono pointer-events-none">
              {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
            </div>
          )}

          <MapContainer
            center={SERVICE_AREA_CENTER}
            zoom={SERVICE_AREA_ZOOM}
            maxZoom={21}
            style={{ height: "100%", width: "100%", background: "#e5e7eb", cursor: "crosshair" }}
            scrollWheelZoom
          >
            {showStreetMap && (
              <TileLayer
                key="road"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={21}
              />
            )}
            {showSatellite && (
              <TileLayer
                key="sat"
                attribution="&copy; Google"
                url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                maxZoom={21}
              />
            )}
            {showStreetLabels && (
              <TileLayer
                key="labels"
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                pane="overlayPane"
                maxZoom={21}
              />
            )}
            {showLandmarks && (
              <TileLayer
                key="landmarks"
                attribution="&copy; Google"
                url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
                pane="overlayPane"
                maxZoom={21}
              />
            )}

            {flyTarget && <FlyTo center={flyTarget} zoom={flyZoom} />}

            {/* City boundary layer */}
            {showCityBoundaries && cityGeoJSON && (
              <GeoJSON
                key="xlsx-city-boundaries"
                data={cityGeoJSON}
                interactive={false}
                style={{
                  color: "#1e40af",
                  weight: 3,
                  fillColor: "#3b82f6",
                  fillOpacity: 0.04,
                  dashArray: "6 4",
                }}
                onEachFeature={(feature, layer) => {
                  const name = feature.properties.city_name || "";
                  const shortName = name.replace(/^City of /i, "");
                  layer.bindTooltip(shortName, {
                    permanent: true,
                    direction: "center",
                    className: "ni-city-label-tooltip",
                  });
                }}
              />
            )}

            {/* Barangay boundary layer */}
            {showBrgyBoundaries && brgyGeoJSON && (
              <GeoJSON
                key="xlsx-brgy-boundaries"
                data={brgyGeoJSON}
                interactive={false}
                style={{
                  color: "#6b7280",
                  weight: 1,
                  fillColor: "#d1d5db",
                  fillOpacity: 0.08,
                }}
              />
            )}

            {/* Barangay name labels */}
            {showBrgyLabels && brgyGeoJSON && (
              <BrgyLabels data={brgyGeoJSON} />
            )}

            {pin && (
              <Marker
                position={[pin.lat, pin.lng]}
                icon={pinIcon}
                draggable={!saving}
                eventHandlers={{
                  dragend(e) {
                    const ll = e.target.getLatLng();
                    setPin({ lat: ll.lat, lng: ll.lng });
                    setEditLocConfirm(false);
                  },
                }}
              >
                <Popup>
                  <div className="text-xs space-y-0.5">
                    <p className="font-bold text-gray-800">{record.customer_full_name}</p>
                    {address && <p className="text-gray-600">{address}</p>}
                    <p className="text-gray-400 font-mono">{pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}</p>
                  </div>
                </Popup>
              </Marker>
            )}

            <ZoomWatcher
              onZoomChange={(z) => {
                setShowLandmarks(z >= 17);
                setShowSatellite(z >= 20);
              }}
            />
            <MapClickToPlace
              active={!saving}
              onPlaced={(lat, lng) => {
                setPin({ lat, lng });
                setEditLocConfirm(false);
              }}
            />
          </MapContainer>
        </div>

        {/* ── Right panel ── */}
        <div className="w-56 flex-shrink-0 flex flex-col min-h-0">
          {/* Tab headers */}
          <div className="flex border-b border-gray-200 bg-white rounded-t-lg shadow-sm flex-shrink-0">
            {[
              { key: "layers", label: "Layers" },
              { key: "subscriber", label: "Subscriber" },
            ].map((t) => {
              const active = rightTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setRightTab(t.key)}
                  className={`flex-1 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide transition-colors border-b-2 ${
                    active
                      ? "text-blue-600 border-blue-600 bg-blue-50/40"
                      : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab body */}
          <div className="bg-white rounded-b-lg shadow flex-1 overflow-y-auto p-4">

            {rightTab === "layers" && (
              <div className="space-y-1.5">
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Layers</div>
                {[
                  { label: "Street Map", val: showStreetMap, set: setShowStreetMap },
                  { label: "Satellite", val: showSatellite, set: setShowSatellite },
                  { label: "Street/Road Names", val: showStreetLabels, set: setShowStreetLabels },
                  { label: "Landmarks", val: showLandmarks, set: setShowLandmarks },
                  { label: "City Boundaries", val: showCityBoundaries, set: setShowCityBoundaries },
                  { label: "Barangay Boundaries", val: showBrgyBoundaries, set: setShowBrgyBoundaries },
                  { label: "Barangay Names", val: showBrgyLabels, set: setShowBrgyLabels },
                ].map(({ label, val, set }) => (
                  <label key={label} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={val}
                      onChange={(e) => set(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    {label}
                  </label>
                ))}
              </div>
            )}

            {rightTab === "subscriber" && (
              <div className="space-y-3">
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Subscriber Info</div>
                {[
                  { label: "Name", value: record.customer_full_name },
                  { label: "Account #", value: record.account_number },
                  { label: "JO #", value: record.jo_number },
                  { label: "Municipality", value: record.municipality },
                  { label: "Barangay", value: record.barangay },
                  { label: "Mobile", value: record.customer_mobile_number },
                  { label: "Plan", value: record.plan },
                  { label: "Address", value: address || record.complete_address },
                ].filter(({ value }) => value).map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
                    <p className="text-xs text-gray-800 break-words">{value}</p>
                  </div>
                ))}
                <div>
                  <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide">Pin Status</p>
                  <p className={`text-xs font-medium ${record.location_saved ? "text-green-700" : "text-red-600"}`}>
                    {record.location_saved ? "✓ Location saved" : "Not yet confirmed"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
